import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Heart, Share2, MapPin, Sparkles, MessageCircle, ShieldCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AppHeader } from "@/components/AppHeader";
import { ResaleLoop } from "@/components/ResaleLoop";
import { getListing, toggleSave, startConversation, CURRENT_USER } from "@/data/listings";
import { useToast } from "@/hooks/use-toast";

const conditionLabel: Record<string, string> = {
  like_new: "Like New ✨",
  excellent: "Excellent",
  good: "Good",
  fair: "Fair",
};

export default function ListingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentImg, setCurrentImg] = useState(0);
  const [saved, setSaved] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);

  const { data: listing, isLoading } = useQuery({
    queryKey: ["listing", id],
    queryFn: () => getListing(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen pb-20 md:pb-8">
        <AppHeader />
        <div className="max-w-4xl mx-auto px-4 mt-6">
          <Skeleton className="h-[50vh] w-full rounded-2xl mb-4" />
          <Skeleton className="h-6 w-2/3 mb-2" />
          <Skeleton className="h-4 w-1/3" />
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-3">👗</div>
          <h2 className="font-semibold text-lg">Listing not found</h2>
          <Link to="/" className="text-sm text-muted-foreground hover:underline mt-2 inline-block">← Back to Discover</Link>
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    const nowSaved = await toggleSave(listing.id, CURRENT_USER.id);
    setSaved(nowSaved);
    toast({ title: nowSaved ? "Saved to wishlist 💕" : "Removed from wishlist" });
  };

  const handleChat = async (quickMessage?: string) => {
    setChatLoading(true);
    const msg = quickMessage || "Hi! Is this dress still available?";
    const conv = await startConversation(listing.id, listing.sellerId, CURRENT_USER.id, msg);
    setChatLoading(false);
    navigate(`/messages?conv=${conv.id}`);
  };

  const discount = Math.round(((listing.originalPrice - listing.sellingPrice) / listing.originalPrice) * 100);

  return (
    <div className="min-h-screen pb-28 md:pb-8">
      <AppHeader />

      <div className="max-w-4xl mx-auto px-4 mt-4">
        {/* Back */}
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
          <ArrowLeft size={14} />
          Back
        </button>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
          {/* Images */}
          <div>
            <div className="relative aspect-[3/4] rounded-3xl overflow-hidden bg-muted mb-3">
              <img
                src={listing.images[currentImg]}
                alt={listing.title}
                className="w-full h-full object-cover"
              />
              {listing.enhanced && (
                <div className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm text-white text-xs">
                  <Sparkles size={11} />
                  AI Ghost Mannequin
                </div>
              )}
              {listing.images.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentImg(i => Math.max(0, i - 1))}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={() => setCurrentImg(i => Math.min(listing.images.length - 1, i + 1))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </>
              )}
            </div>
            {/* Thumbnails */}
            {listing.images.length > 1 && (
              <div className="flex gap-2">
                {listing.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImg(i)}
                    className={`w-14 h-14 rounded-xl overflow-hidden border-2 transition-all ${
                      i === currentImg ? "border-rose-400" : "border-transparent opacity-60"
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            {/* Header */}
            <div className="flex items-start justify-between mb-1">
              <Badge variant="outline" className="text-xs capitalize mb-2">{listing.category}</Badge>
              <div className="flex gap-2">
                <button onClick={handleSave} className={`p-2 rounded-full border transition-all ${saved ? "bg-rose-50 border-rose-200 text-rose-500" : "border-border/60 text-muted-foreground hover:text-foreground"}`}>
                  <Heart size={16} fill={saved ? "currentColor" : "none"} />
                </button>
                <button className="p-2 rounded-full border border-border/60 text-muted-foreground hover:text-foreground transition-all">
                  <Share2 size={16} />
                </button>
              </div>
            </div>

            <h1 className="text-xl font-bold text-foreground leading-snug mb-3">{listing.title}</h1>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-4">
              <span className="text-3xl font-bold" style={{ color: "var(--rose)" }}>{listing.sellingPrice} SAR</span>
              <span className="text-sm text-muted-foreground line-through">{listing.originalPrice} SAR</span>
              <span className="text-sm font-medium px-2 py-0.5 rounded-full bg-rose-50 text-rose-600">-{discount}%</span>
            </div>

            {/* Details grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              {[
                { label: "Size", value: listing.size },
                { label: "Condition", value: conditionLabel[listing.condition] },
                { label: "Brand", value: listing.brand || "Unbranded" },
                { label: "Views", value: `${listing.views} views` },
              ].map(({ label, value }) => (
                <div key={label} className="p-3 rounded-xl bg-muted/40 border border-border/40">
                  <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
                  <p className="text-sm font-medium">{value}</p>
                </div>
              ))}
            </div>

            {/* Event context */}
            {listing.eventWornAt && (
              <div className="flex items-center gap-2 p-3 rounded-xl mb-4" style={{ background: "var(--rose-light)" }}>
                <span className="text-lg">💃</span>
                <div>
                  <p className="text-xs text-muted-foreground">Worn at</p>
                  <p className="text-sm font-medium">{listing.eventWornAt}</p>
                </div>
              </div>
            )}

            {/* Description */}
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">{listing.description}</p>

            {/* Seller */}
            <div className="flex items-center justify-between p-3 rounded-xl border border-border/60 bg-card mb-4">
              <div className="flex items-center gap-3">
                <img src={listing.sellerAvatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <p className="text-sm font-medium">{listing.sellerName}</p>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin size={10} />
                    <span>{listing.location} · {listing.distanceKm} km away</span>
                  </div>
                </div>
              </div>
              <ShieldCheck size={16} style={{ color: "var(--rose)" }} />
            </div>

            {/* Resale loop */}
            <div className="mb-5">
              <ResaleLoop wornCount={listing.wornCount} />
            </div>

            {/* CTA */}
            <div className="space-y-2">
              <Button
                className="w-full rounded-full h-12 text-sm font-semibold gap-2"
                style={{ background: "var(--rose)", color: "white" }}
                onClick={() => handleChat()}
                disabled={chatLoading}
              >
                <MessageCircle size={16} />
                {chatLoading ? "Opening chat…" : "Chat with Seller"}
              </Button>

              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: "Is this available?", msg: "Hi! Is this dress still available?" },
                  { label: "Can you lower price?", msg: "Hi! I love this dress. Would you consider a small discount?" },
                ].map(({ label, msg }) => (
                  <Button
                    key={label}
                    variant="outline"
                    size="sm"
                    className="rounded-full text-xs h-9 border-border/60"
                    onClick={() => handleChat(msg)}
                    disabled={chatLoading}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
